/**
 * Detector de despliegues nuevos.
 *
 * Vigila la version del propio host y la de los microfrontends que esten
 * cargados en la pagina (lexon, centinela), y avisa cuando alguna difiere de la
 * que se cargo al abrir. NO recarga nada por su cuenta: la recarga la decide el
 * usuario, que puede tener trabajo a medias (un correo redactandose, una
 * clasificacion en curso). Recargar por sorpresa fue justamente lo que QA veto
 * en 2026 y por lo que se retiro el versionChecker original.
 *
 * Depende de PEC-8214: config.js se sirve no-store y el bundle del MF no-cache.
 * Sin eso este detector leeria valores cacheados — y en el peor caso avisaria de
 * una version que la recarga del usuario no llegaria a traer.
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * El chart common anade window.RELEASE al FINAL de config.js, despues de
 * configValues, para que gane por orden de evaluacion. Por eso se lee la ULTIMA
 * coincidencia: leer la primera fue la causa del bucle de recargas de 2026
 * (652bdac0), cuando react_{env}.yaml aun traia un 'RC1' estatico delante.
 */
function parseRelease(configJs) {
  const matches = [...configJs.matchAll(/window\.RELEASE\s*=\s*['"]([^'"]+)['"]/g)];
  return matches.length ? matches[matches.length - 1][1] : null;
}

/**
 * Microfrontends vigilados. La version CARGADA la publica el propio bundle en un
 * window.* propio (no window.RELEASE, que pisaria el del host); la DESPLEGADA se
 * consulta en el version.json de su origen, porque el host carga el MF con
 * <script src> y nunca llega a cargar su index.html ni su config.js.
 */
const MICROFRONTENDS = [
  { name: 'lexon', loadedVersionVar: 'RELEASE_LEXON', scriptUrlVar: 'URL_MF_LEXON' },
  { name: 'centinela', loadedVersionVar: 'RELEASE_CENTINELA', scriptUrlVar: 'URL_MF_CENTINELA' },
];

/**
 * Cada consulta se aisla: una fuente caida no puede tapar el cambio detectado en
 * otra. Devolver null (y no propagar) es lo que permite que un aviso del host
 * sobreviva a que un conector todavia no sirva version.json.
 */
async function safeFetch(fn) {
  try {
    return await fn();
  } catch {
    // Red caida, CORS, JSON invalido: se reintenta en el siguiente ciclo. Ante la
    // duda no se avisa — un falso positivo le cuesta al usuario una recarga
    // inutil y le hace desconfiar del aviso.
    return null;
  }
}

async function fetchDeployedHostVersion() {
  return safeFetch(async () => {
    const res = await fetch('/config.js', { cache: 'no-store' });
    return res.ok ? parseRelease(await res.text()) : null;
  });
}

async function fetchDeployedMfVersion(scriptUrl) {
  return safeFetch(async () => {
    const { origin } = new URL(scriptUrl, window.location.href);
    const res = await fetch(`${origin}/version.json`, { cache: 'no-store' });
    if (!res.ok) return null;
    // Un MF con imagen anterior a este cambio no tiene version.json: el fallback
    // SPA de su nginx devuelve 200 con index.html. Comprobar el content-type
    // evita depender de que reviente el parseo para descartarlo.
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('json')) return null;
    const data = await res.json();
    return data && data.version ? data.version : null;
  });
}

/**
 * Arranca la vigilancia. Comprueba cada POLL_INTERVAL_MS y al volver el foco a la
 * pestana (el caso realista: alguien retoma la app tras un rato y se entera al
 * momento, sin esperar al siguiente ciclo).
 *
 * @param {(change: {source: string, from: string, to: string}) => void} onNewVersion
 * @returns {() => void} cleanup
 */
export function startVersionWatcher(onNewVersion) {
  // Kill switch de runtime (window.VERSION_NOTIFIER_ENABLED, inyectado por el
  // ConfigMap). Permite apagar el aviso en caliente cambiando un valor en Helm,
  // sin rebuild ni rollback — importante porque el aviso viaja en el mismo
  // despliegue que el fix de cache y no queremos que un problema en uno obligue
  // a revertir el otro. Solo 'false' apaga: si la var falta, el aviso sigue vivo.
  if (String(window.VERSION_NOTIFIER_ENABLED).toLowerCase() === 'false') {
    return () => {};
  }

  // La version del host no cambia mientras la pagina vive: sirve de referencia
  // fija. Comparar contra ella (y no contra la comprobacion anterior) evita
  // falsos avisos durante un rolling deploy, cuando conviven pod viejo y nuevo.
  const bootHostVersion = window.RELEASE || null;
  let lastNotified = null;
  let checking = false;

  const check = async () => {
    if (checking || document.hidden) return;
    checking = true;
    try {
      const changes = [];
      // Cada fuente se consulta por separado y ya viene aislada (safeFetch): un
      // conector inaccesible no impide avisar de un cambio en el host.

      const deployedHost = await fetchDeployedHostVersion();
      if (bootHostVersion && deployedHost && deployedHost !== bootHostVersion) {
        changes.push({ source: 'host', from: bootHostVersion, to: deployedHost });
      }

      for (const mf of MICROFRONTENDS) {
        // Solo se vigila un MF si esta cargado: si el usuario nunca abre el panel,
        // la proxima vez que lo abra recibira la version fresca sin necesidad de
        // avisar de nada.
        const loaded = window[mf.loadedVersionVar];
        const scriptUrl = window[mf.scriptUrlVar];
        if (!loaded || !scriptUrl) continue;

        const deployed = await fetchDeployedMfVersion(scriptUrl);
        if (deployed && deployed !== loaded) {
          changes.push({ source: mf.name, from: loaded, to: deployed });
        }
      }

      // Un unico aviso aunque hayan cambiado varios: una sola recarga los trae
      // todos (recargar la pagina reinyecta tambien los microfrontends).
      const change = changes[0];
      if (change && change.to !== lastNotified) {
        lastNotified = change.to;
        onNewVersion(change);
      }
    } catch {
      // Red de seguridad para lo que no venga de un fetch (p.ej. una URL de MF
      // mal formada en config.js). Las consultas ya se aislan una a una.
    } finally {
      checking = false;
    }
  };

  const onVisibilityChange = () => {
    if (!document.hidden) check();
  };

  const timer = setInterval(check, POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
